import {gql, useQuery} from '@apollo/client';
import React, {useState} from 'react';
import {LoadingSpinner, Paths, RelayConnection, SimpleDropdown, ErrorDisplay} from "../../../common";
import {SortOrder} from "../domain";
import {
    SubmittedRequestColumn, SubmittedRequestInfo,
    SubmittedRequestSortBy
} from "./SubmittedRequestDomain";
import {RelayPagination, RelayPaginationState, useRelayPagination} from "../../pagination";
import {CaretDownIcon, CaretUpIcon, Icon, Pane, Text} from "evergreen-ui";
import {SubmittedRequestPreview} from "./SubmittedRequestPreview";
import {NavigationSearchBar} from "../../../placement/components/NavigationSearchBar";
import {asQueryParam} from "./SubmittedRequestListFilter";
import {SubmittedRequestListFilterBadge} from "./SubmittedRequestListFilterBadge";
import {SubmittedRequestListFilter} from "./SubmittedRequestListFilter";

import {useExportData} from "../export/useExportData"
import {ExportButton} from "common/component/ExportButton";
import {useObservable} from "rxsupport";
import {ExportRequestModal} from "../export/ExportRequestModal";

export const SUBMITTED_REQUEST_QUERY = gql`
    query CaseSubmitRequestQuery(
        $first: Int = 50
        $after: String
        $clientGuid: String
        $authorizationGuid: String,
        $params: CaseSubmitRequestQueryParamsInput!, 
        $search: String
    ) {
        caseSubmitRequestQuery(
            clientGuid: $clientGuid
            authorizationGuid: $authorizationGuid
            first: $first
            after: $after
            params: $params
            search: $search
        ) {
            totalElements
            pageInfo {
                hasNextPage
                hasPreviousPage
                startCursor
                endCursor
            }
            edges {
                node {
                    guid, authorizationGuid, caseNumber, clientGuid, clientName,
                    closedDate, origin, reason, recordType,
                    requestType, sfAuthId, sfId, sfClientId,
                    providerGuid, status, submittedDate, submitterName
                }
            }
        }
    }
`;

const EXPORT_SUBMITTED_REQUESTS = gql`
    mutation ($filters: String) {
        exportCaseSubmitRequests(filters: $filters) {
            guid
        }
    }
`;

type SubmittedRequestNode = {
    guid: string;
    caseNumber: string;
    clientName: string;
    clientGuid: string;
    requestType: string;
    submittedDate: string;
    submitterName: string;
    status: string;
    closedDate: string | null;
    [key: string]: any;
};

type SubmittedRequestEdge = {
    node: SubmittedRequestNode;
    cursor: string;
};

type SubmittedRequestConnection = RelayConnection<SubmittedRequestNode> & {
    totalElements: number;
};

export type SubmittedRequestQueryResponse = {
    caseSubmitRequestQuery: SubmittedRequestConnection;
};

export const SubmittedRequestListColumn: Readonly<SubmittedRequestColumn[]> = [
    {name: 'Client Name', attr: 'clientName'},
    {name: 'Request Type', attr: 'requestType'},
    {name: 'Request Number', attr: 'caseNumber'},
    {name: 'Submitted Date', attr: 'submittedDate'},
    {name: 'Submitted By', attr: 'submitterName'},
    {name: 'Status', attr: 'status'},
    {name: 'Closed Date', attr: 'closedDate'},
] as const;


export type SubmittedRequestListProps = {
    selectedRequest: any | null;
    sortBy: SubmittedRequestSortBy;
    pagination: RelayPaginationState;
    filterValue: any;
    pageSize: number;
    dispatch: React.Dispatch<any>;
    searchText: string;
};


export const SubmittedRequestList = ({
    selectedRequest,
    sortBy,
    pagination,
    dispatch,
    filterValue,
    searchText,
    pageSize
}: SubmittedRequestListProps) => {
    const {loading, data, error} = useQuery<SubmittedRequestQueryResponse>(
        SUBMITTED_REQUEST_QUERY,
        {
            variables: {
                ...pagination.toGraphQlParams(),
                clientGuid: filterValue.clientGuid || null,
                authorizationGuid: filterValue.authorizationGuid || null,
                search: searchText,
                params: {
                    ...asQueryParam(filterValue),
                    sortDir: sortBy.order,
                    sortBy: sortBy.column,
                },
            }
        }
    );

    const {isRunning$: isExporting$, execute: exportData} = useExportData(EXPORT_SUBMITTED_REQUESTS);
    const isExporting = useObservable(isExporting$, false);
    const [exportGuid, setExportGuid] = useState<string | null>(null);
    // Export the current filtered view — sends the same filter snapshot the grid renders, then
    // opens the progress modal to poll for and download the generated file.
    const handleExport = async () => {
        const guid = await exportData(JSON.stringify({
            ...asQueryParam(filterValue),
            search: searchText,
            sortDir: sortBy.order,
            sortBy: sortBy.column
        }));
        if (guid) {
            setExportGuid(guid);
        }
    };

    const setSortByColumn = (newSortColumn: Paths<SubmittedRequestInfo>) => {
        // ignore sort by service types
        if (['NONE'].includes(newSortColumn)) {
            return;
        }

        let newSortOrder = SortOrder.DESC;
        if (newSortColumn === sortBy.column && sortBy.order === newSortOrder) {
            newSortOrder = SortOrder.ASC;
        }

        dispatch({type: 'SET_SORT_BY', sortBy: {column: newSortColumn, order: newSortOrder}});
    }

    // data?.caseSubmitRequestQuery
    let totalElements: number = 0;
    if (data?.caseSubmitRequestQuery?.pageInfo?.endCursor !== null) {
        totalElements = data?.caseSubmitRequestQuery?.totalElements ?? 0;
    }
    const {onPaginationClick} = useRelayPagination(dispatch, totalElements);

    if (error) {
        return <ErrorDisplay error={error} title="Error loading submitted requests" />;
    }

    return (
        <Pane className="h-full pt-5 m-5 bg-white shadow-md flex flex-col" minHeight="calc(100vh - 100px - 260px - 40px)">
            <Pane display='flex' justifyContent='flex-end' flex="0 0" paddingBottom={20}>
                <div className="flex">
                    <NavigationSearchBar placeholder={"Search by Number, Client Name"}
                                         searchQuery={searchText}
                                         setSearchQuery={(search) => dispatch({
                                             type: 'SET_SEARCH',
                                             search: search
                                         })}/>

                    <SubmittedRequestListFilter initFilterValue={filterValue} dispatch={dispatch}/>

                    <div className="mr-3 flex items-center">
                        <ExportButton
                            onClick={handleExport}
                            disabled={isExporting}
                            minWidth="7rem"
                        />
                        <ExportRequestModal guid={exportGuid} onClose={() => setExportGuid(null)}/>
                    </div>

                    <SimpleDropdown options={["10", "50", "100"]} selected={pageSize.toString()}
                                    minWidth={"2rem"}
                                    textColor={"gray-700"}
                                    setSelected={(newPageSize) => dispatch({
                                        type: "SET_PAGE_SIZE",
                                        pageSize: parseInt(newPageSize)
                                    })}/>
                    <RelayPagination pageInfo={data?.caseSubmitRequestQuery?.pageInfo} count={totalElements}
                                     onClick={onPaginationClick}/>
                </div>
            </Pane>
            <SubmittedRequestListFilterBadge filter={filterValue} dispatch={dispatch}/>

            <div className="overflow-auto flex flex-col flex-grow mb-4 scrollbar-always">
                <table className="table-fixed w-full">
                    <thead>
                    <tr className='text-left select-none text-sm text-gray-portal-table-title'>
                        {
                            SubmittedRequestListColumn.map((column, index) => (
                                <th
                                    key={column.attr}
                                    className={'font-light' + (index === 0 ? ' pl-8' : '')}
                                    onClick={() => setSortByColumn(column.attr)}
                                >
                                    <div className='flex items-center'>
                                        <div>
                                            {column.name}
                                        </div>
                                        <div className='w-6 h-6 pl-2'>
                                            {sortBy.column === column.attr && sortBy.order === SortOrder.ASC &&
                                                <Icon className='align-middle' icon={CaretUpIcon}/>}
                                            {sortBy.column === column.attr && sortBy.order === SortOrder.DESC &&
                                                <Icon className='align-middle' icon={CaretDownIcon}/>}
                                        </div>
                                    </div>
                                </th>
                            ))
                        }
                    </tr>
                    </thead>
                    <tbody>
                    {
                        !loading && data?.caseSubmitRequestQuery.edges.map(({node}) => (
                            <SubmittedRequestPreview
                                columns={SubmittedRequestListColumn}
                                key={node.guid}
                                request={node}
                                isSelected={selectedRequest?.guid === node.guid}
                                onClick={() => dispatch({type: 'SELECT', payload: node})}
                            />
                        ))
                    }
                    </tbody>
                </table>
                {
                    loading &&
                    <div className="w-full h-full flex flex-grow items-center">
                        <LoadingSpinner spacing={20} height={"100%"}/>
                    </div>
                }
                {
                    totalElements === 0 && !loading && <div className="w-full h-full flex flex-grow items-center">
                        <div className="m-auto">
                            No requests matched the searching criteria.
                        </div>
                    </div>
                }
            </div>
        </Pane>
    );
};
